import { API_BASE } from '@/shared/services/api';
import { useTranslation } from "react-i18next";import React, { useState, useEffect } from 'react';
import { BarChart3, Download, Calendar } from 'lucide-react';
import { Button } from '@/shared/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/components/ui/card';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell } from
'recharts';

const API_URL = API_BASE;

// Default data for initial render
const defaultMonthlyData = [
{ month: 'Jan', deliveries: 0, revenue: 0 },
{ month: 'Feb', deliveries: 0, revenue: 0 },
{ month: 'Mar', deliveries: 0, revenue: 0 },
{ month: 'Apr', deliveries: 0, revenue: 0 },
{ month: 'May', deliveries: 0, revenue: 0 },
{ month: 'Jun', deliveries: 0, revenue: 0 }];


const defaultStatusData = [
{ name: 'Delivered', value: 0, color: 'hsl(var(--success))' },
{ name: 'In Transit', value: 0, color: 'hsl(var(--info))' },
{ name: 'Pending', value: 0, color: 'hsl(var(--warning))' },
{ name: 'Failed', value: 0, color: 'hsl(var(--destructive))' }];


export const Reports = () => {const { t } = useTranslation("ezparcel");
  const [monthlyData, setMonthlyData] = useState(defaultMonthlyData);
  const [statusData, setStatusData] = useState(defaultStatusData);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchReportData = async () => {
      try {
        const [monthlyRes, statusRes] = await Promise.all([
        fetch(`${API_URL}/api/reports/monthly-stats`),
        fetch(`${API_URL}/api/reports/status-distribution`)]
        );

        if (monthlyRes.ok) {
          const data = await monthlyRes.json();
          if (data && data.length > 0) {
            setMonthlyData(data);
          }
        }

        if (statusRes.ok) {
          const data = await statusRes.json();
          if (data && data.length > 0) {
            // Map with colors
            setStatusData(data.map((item, idx) => ({
              ...item,
              color: ['hsl(var(--success))', 'hsl(var(--info))', 'hsl(var(--warning))', 'hsl(var(--destructive))'][idx] || 'hsl(var(--muted))'
            })));
          }
        }
      } catch (error) {
        console.error('Failed to fetch report data:', error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchReportData();
  }, []);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">{t("reportsAnalytics", "Reports & Analytics")}</h1>
          <p className="text-muted-foreground mt-2">{t("comprehensiveInsightsInto", "Comprehensive insights into your logistics operations")}

          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline">
            <Calendar className="mr-2 h-4 w-4" />{t("dateRange", "Date Range")}

          </Button>
          <Button className="bg-[hsl(var(--accent))] hover:bg-[hsl(var(--accent-hover))]">
            <Download className="mr-2 h-4 w-4" />{t("exportReport", "Export Report")}

          </Button>
        </div>
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        <Card className="card-elevated">
          <CardHeader>
            <CardTitle>{t("monthlyPerformance", "Monthly Performance")}</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={monthlyData}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="month" stroke="hsl(var(--muted-foreground))" />
                <YAxis stroke="hsl(var(--muted-foreground))" />
                <Tooltip
                  contentStyle={{
                    backgroundColor: 'hsl(var(--card))',
                    border: '1px solid hsl(var(--border))',
                    borderRadius: '8px'
                  }} />
                
                <Line
                  type="monotone"
                  dataKey="deliveries"
                  stroke="hsl(var(--accent))"
                  strokeWidth={2}
                  dot={{ fill: 'hsl(var(--accent))' }} />
                
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card className="card-elevated">
          <CardHeader>
            <CardTitle>{t("revenueTrend", "Revenue Trend")}</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={monthlyData}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="month" stroke="hsl(var(--muted-foreground))" />
                <YAxis stroke="hsl(var(--muted-foreground))" />
                <Tooltip
                  contentStyle={{
                    backgroundColor: 'hsl(var(--card))',
                    border: '1px solid hsl(var(--border))',
                    borderRadius: '8px'
                  }} />
                
                <Bar dataKey="revenue" fill="hsl(var(--success))" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card className="card-elevated">
          <CardHeader>
            <CardTitle>{t("shipmentStatusDistribution", "Shipment Status Distribution")}</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={statusData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                  outerRadius={100}
                  fill="#8884d8"
                  dataKey="value">
                  
                  {statusData.map((entry, index) =>
                  <Cell key={`cell-${index}`} fill={entry.color} />
                  )}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card className="card-elevated">
          <CardHeader>
            <CardTitle>{t("keyMetrics", "Key Metrics")}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-center justify-between p-3 border border-border rounded-lg">
                <span className="text-sm font-medium">{t("averageDeliveryTime", "Average Delivery Time")}</span>
                <span className="text-lg font-bold">{t("23Hrs", "2.3 hrs")}</span>
              </div>
              <div className="flex items-center justify-between p-3 border border-border rounded-lg">
                <span className="text-sm font-medium">{t("customerSatisfaction", "Customer Satisfaction")}</span>
                <span className="text-lg font-bold text-green-600">{t("4850", "4.8/5.0")}</span>
              </div>
              <div className="flex items-center justify-between p-3 border border-border rounded-lg">
                <span className="text-sm font-medium">{t("failedDeliveryRate", "Failed Delivery Rate")}</span>
                <span className="text-lg font-bold">{t("06", "0.6%")}</span>
              </div>
              <div className="flex items-center justify-between p-3 border border-border rounded-lg">
                <span className="text-sm font-medium">{t("vehicleUtilization", "Vehicle Utilization")}</span>
                <span className="text-lg font-bold">{t("87", "87%")}</span>
              </div>
              <div className="flex items-center justify-between p-3 border border-border rounded-lg">
                <span className="text-sm font-medium">{t("averageCostPer", "Average Cost per Delivery")}</span>
                <span className="text-lg font-bold">{t("1250", "$12.50")}</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>);

};